import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getDepartments } from '@/services/department.service';
import { getEmployees } from '@/services/employee.service';
import candidateService from '@/services/candidate.service';
import { getJobPostings } from '@/services/job.service';
import { interviewService } from '@/services/interview.service';
import { getAllOnboardings } from '@/services/onboarding.service';
import { useAuthStore } from './authStore';

interface SharedDataState {
  // State
  departments: any[];
  employees: any[];
  candidates: any[];
  jobs: any[];
  interviews: any[];
  onboardings: any[];
  loading: boolean;
  isInitializing: boolean;
  progress: number;
  error: string | null;

  // Cache timestamps
  lastFetched: {
    departments: number | null;
    employees: number | null;
    candidates: number | null;
    jobs: number | null;
    interviews: number | null;
    onboardings: number | null;
  };

  // Actions
  refreshDepartments: () => Promise<void>;
  refreshEmployees: () => Promise<void>;
  refreshCandidates: () => Promise<void>;
  refreshJobs: () => Promise<void>;
  refreshInterviews: () => Promise<void>;
  refreshOnboardings: () => Promise<void>;
  refreshAllData: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Track in-flight requests to avoid duplication
const inFlightRequests: Record<string, Promise<void> | null> = {};

// Helper function to check if data is fresh - uses ONLY timestamp, not array length
// (empty array can be a valid response!)
const isDataFresh = (lastFetched: number | null): boolean => {
  if (!lastFetched) return false;
  return Date.now() - lastFetched < CACHE_DURATION;
};

// Helper function to process employee data
const processEmployeeData = (employeesData: any[]) => {
  return employeesData.map((emp: any) => {
    // Ensure employee has proper structure
    const processed = {
      ...emp,
      _id: emp._id || emp.id,
      // Handle user object structure
      user: emp.user || {
        _id: emp._id || emp.id,
        firstName: emp.firstName,
        lastName: emp.lastName,
        email: emp.email,
        roles: emp.roles || []
      },
      // Ensure firstName and lastName are available
      firstName: emp.firstName || emp.user?.firstName,
      lastName: emp.lastName || emp.user?.lastName,
      // Handle department structure
      department: emp.department || emp.user?.department,
      // Handle role structure
      role: emp.role || emp.user?.role,
      // Ensure status is available
      status: emp.status || 'Active',
      // Preserve profile picture
      profilePicture: emp.profilePicture
    };
    return processed;
  });
};

// Helper function to extract data from API responses
const extractDataFromResponse = (response: any): any[] => {
  if (!response) return [];

  if (Array.isArray(response)) {
    return response;
  } else if (response.data && Array.isArray(response.data)) {
    return response.data;
  } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }

  return [];
};

export const useSharedDataStore = create<SharedDataState>()(
  devtools(
    (set, get) => ({
      // Initial state
      departments: [],
      employees: [],
      candidates: [],
      jobs: [],
      interviews: [],
      onboardings: [],
      loading: false,
      isInitializing: false,
      progress: 0,
      error: null,

      // Cache timestamps
      lastFetched: {
        departments: null,
        employees: null,
        candidates: null,
        jobs: null,
        interviews: null,
        onboardings: null,
      },

      // Setter methods
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),

      // Fetch departments
      refreshDepartments: async () => {
        const { hasPermission } = useAuthStore.getState();
        if (!hasPermission('DEPARTMENT_VIEW') && !hasPermission('DEPARTMENT_EDIT') && !hasPermission('DEPARTMENT_CREATE')) {
          set({ departments: [] }); // Clear if no permission
          return;
        }

        const state = get();
        if (isDataFresh(state.lastFetched.departments)) return;

        // Return existing promise if already fetching
        if (inFlightRequests['departments']) return inFlightRequests['departments'];

        inFlightRequests['departments'] = (async () => {
          try {
            set({ loading: true });
            const response = await getDepartments();
            const departmentsData = response.data?.data || response.data || [];
            set({
              departments: departmentsData,
              lastFetched: { ...get().lastFetched, departments: Date.now() }
            });
          } catch (err) {
            set({ error: 'Failed to fetch departments' });
            console.error('Error fetching departments:', err);
          } finally {
            inFlightRequests['departments'] = null;
            // Only toggle loading if it's the last thing running
            if (!Object.values(inFlightRequests).some(p => p !== null)) {
              set({ loading: false });
            }
          }
        })();

        return inFlightRequests['departments'];
      },

      // Fetch employees
      refreshEmployees: async () => {
        const { hasPermission } = useAuthStore.getState();
        if (!hasPermission('EMPLOYEES_VIEW') && !hasPermission('EMPLOYEES_EDIT') && !hasPermission('EMPLOYEES_CREATE')) {
          set({ employees: [] }); // Clear if no permission
          return;
        }

        const state = get();
        if (isDataFresh(state.lastFetched.employees)) return;
        if (inFlightRequests['employees']) return inFlightRequests['employees'];

        inFlightRequests['employees'] = (async () => {
          try {
            set({ loading: true });
            const response = await getEmployees();
            const employeesData = response.data || response || [];
            const processedEmployees = processEmployeeData(Array.isArray(employeesData) ? employeesData : []);
            set({
              employees: processedEmployees,
              lastFetched: { ...get().lastFetched, employees: Date.now() }
            });
          } catch (err) {
            set({ error: 'Failed to fetch employees' });
            console.error('Error fetching employees:', err);
          } finally {
            inFlightRequests['employees'] = null;
            if (!Object.values(inFlightRequests).some(p => p !== null)) {
              set({ loading: false });
            }
          }
        })();

        return inFlightRequests['employees'];
      },

      // Fetch candidates
      refreshCandidates: async () => {
        const { hasPermission } = useAuthStore.getState();
        if (!hasPermission('CANDIDATE_VIEW') && !hasPermission('CANDIDATE_EDIT') && !hasPermission('CANDIDATE_CREATE')) {
          set({ candidates: [] });
          return;
        }

        const state = get();
        if (isDataFresh(state.lastFetched.candidates)) return;
        if (inFlightRequests['candidates']) return inFlightRequests['candidates'];

        inFlightRequests['candidates'] = (async () => {
          try {
            set({ loading: true });
            const response = await candidateService.getCandidates({ limit: 100 });
            const candidatesData = extractDataFromResponse(response);
            set({
              candidates: candidatesData,
              lastFetched: { ...get().lastFetched, candidates: Date.now() }
            });
          } catch (err) {
            set({ error: 'Failed to fetch candidates' });
            console.error('Error fetching candidates:', err);
          } finally {
            inFlightRequests['candidates'] = null;
            if (!Object.values(inFlightRequests).some(p => p !== null)) {
              set({ loading: false });
            }
          }
        })();

        return inFlightRequests['candidates'];
      },

      // Fetch jobs
      refreshJobs: async () => {
        const { hasPermission } = useAuthStore.getState();
        if (!hasPermission('JOB_VIEW') && !hasPermission('JOB_EDIT') && !hasPermission('JOB_CREATE')) {
          set({ jobs: [] });
          return;
        }

        const state = get();
        if (isDataFresh(state.lastFetched.jobs)) return;
        if (inFlightRequests['jobs']) return inFlightRequests['jobs'];

        inFlightRequests['jobs'] = (async () => {
          try {
            set({ loading: true });
            const response = await getJobPostings();
            const jobsData = extractDataFromResponse(response);
            set({
              jobs: jobsData,
              lastFetched: { ...get().lastFetched, jobs: Date.now() }
            });
          } catch (err) {
            set({ error: 'Failed to fetch jobs' });
            console.error('Error fetching jobs:', err);
          } finally {
            inFlightRequests['jobs'] = null;
            if (!Object.values(inFlightRequests).some(p => p !== null)) {
              set({ loading: false });
            }
          }
        })();

        return inFlightRequests['jobs'];
      },

      // Fetch interviews
      refreshInterviews: async () => {
        const { hasPermission } = useAuthStore.getState();
        if (!hasPermission('CANDIDATE_VIEW') && !hasPermission('CANDIDATE_EDIT')) {
          set({ interviews: [] });
          return;
        }

        const state = get();
        if (isDataFresh(state.lastFetched.interviews)) return;
        if (inFlightRequests['interviews']) return inFlightRequests['interviews'];

        inFlightRequests['interviews'] = (async () => {
          try {
            set({ loading: true });
            const response = await interviewService.getInterviews({});
            const interviewsData = Array.isArray(response?.data) ? response.data : [];
            set({
              interviews: interviewsData,
              lastFetched: { ...get().lastFetched, interviews: Date.now() }
            });
          } catch (err) {
            set({ error: 'Failed to fetch interviews' });
            console.error('Error fetching interviews:', err);
            set({ lastFetched: { ...get().lastFetched, interviews: Date.now() - CACHE_DURATION + 30_000 } });
          } finally {
            inFlightRequests['interviews'] = null;
            if (!Object.values(inFlightRequests).some(p => p !== null)) {
              set({ loading: false });
            }
          }
        })();

        return inFlightRequests['interviews'];
      },

      // Fetch onboardings
      refreshOnboardings: async () => {
        const { hasPermission } = useAuthStore.getState();
        if (!hasPermission('ONBOARDING_VIEW')) {
          set({ onboardings: [] });
          return;
        }

        const state = get();
        if (isDataFresh(state.lastFetched.onboardings)) return;
        if (inFlightRequests['onboardings']) return inFlightRequests['onboardings'];

        inFlightRequests['onboardings'] = (async () => {
          try {
            set({ loading: true });
            const response = await getAllOnboardings();
            const onboardingsData = extractDataFromResponse(response);
            set({
              onboardings: onboardingsData,
              lastFetched: { ...get().lastFetched, onboardings: Date.now() }
            });
          } catch (err) {
            set({ error: 'Failed to fetch onboardings' });
            console.error('Error fetching onboardings:', err);
          } finally {
            inFlightRequests['onboardings'] = null;
            if (!Object.values(inFlightRequests).some(p => p !== null)) {
              set({ loading: false });
            }
          }
        })();

        return inFlightRequests['onboardings'];
      },

      // Refresh all data at once
      refreshAllData: async () => {
        const state = get();

        // If already initializing, wait for it
        if (inFlightRequests['all']) return inFlightRequests['all'];

        inFlightRequests['all'] = (async () => {
          try {
            // Only set isInitializing: true if this is the first time or data is very stale
            const needsGlobalInit = !state.lastFetched.employees && !state.lastFetched.candidates;

            if (needsGlobalInit) {
              set({ isInitializing: true, progress: 0 });
            }

            set({ loading: true, error: null });

            const refreshPromises = [];

            // RELY ONLY ON TIMESTAMP - empty arrays are valid and should stay cached
            if (!isDataFresh(state.lastFetched.departments)) {
              refreshPromises.push(get().refreshDepartments());
            }
            if (!isDataFresh(state.lastFetched.employees)) {
              refreshPromises.push(get().refreshEmployees());
            }
            if (!isDataFresh(state.lastFetched.candidates)) {
              refreshPromises.push(get().refreshCandidates());
            }
            if (!isDataFresh(state.lastFetched.jobs)) {
              refreshPromises.push(get().refreshJobs());
            }
            if (!isDataFresh(state.lastFetched.interviews)) {
              refreshPromises.push(get().refreshInterviews());
            }
            if (!isDataFresh(state.lastFetched.onboardings)) {
              refreshPromises.push(get().refreshOnboardings());
            }

            if (refreshPromises.length > 0) {
              // Simulate progress increments for better UX if initializing
              let prog = 0;
              const progInterval = needsGlobalInit ? setInterval(() => {
                prog = Math.min(95, prog + Math.random() * 15);
                set({ progress: prog });
              }, 300) : null;

              await Promise.allSettled(refreshPromises);

              if (progInterval) clearInterval(progInterval);
            }

            if (needsGlobalInit) {
              set({ progress: 100 });
              // Stay at 100 for a brief moment
              await new Promise(r => setTimeout(r, 400));
            }
          } catch (err) {
            set({ error: 'Failed to fetch system data' });
            console.error('Error in refreshAllData:', err);
          } finally {
            inFlightRequests['all'] = null;
            set({ loading: false, isInitializing: false });
          }
        })();

        return inFlightRequests['all'];
      },
    }),
    {
      name: 'shared-data-store',
    }
  )
); 